// Shared display/input rules. API authorization and file validation remain authoritative.
export const MAX_DOCUMENT_BYTES = 25 * 1024 * 1024;
export const DOCUMENT_TYPES = Object.freeze([
  ['agreement', 'Agreement'], ['contract', 'Contract'],
  ['terms_and_conditions', 'Terms & conditions'], ['insurance', 'Insurance'],
  ['certification', 'Certification'], ['price_agreement', 'Price agreement'],
  ['quotation', 'Quotation'], ['receipt', 'Receipt / expense evidence'],
  ['service_report', 'Service report'], ['specification', 'Specification'],
  ['other', 'Other supporting document'],
]);
export const FILE_MIME = Object.freeze({
  pdf: 'application/pdf', jpg: 'image/jpeg', jpeg: 'image/jpeg',
  png: 'image/png', webp: 'image/webp', txt: 'text/plain', csv: 'text/csv',
  doc: 'application/msword',
  docx: 'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
  xls: 'application/vnd.ms-excel',
  xlsx: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
});
export const DOCUMENT_ACCEPT = Object.keys(FILE_MIME).map(ext => `.${ext}`).join(',');
export const DOCUMENT_PERMISSIONS = Object.freeze({
  vendor: { view: 'vendor.vendor.view', upload: 'vendor.vendor.edit' },
  purchase_order: { view: 'procurement.po.view', upload: 'procurement.po.edit' },
  maintenance_task: { view: 'maintenance.task.view', upload: 'maintenance.task.edit' },
  asset: { view: 'maintenance.asset.view', upload: 'maintenance.asset.edit' },
});

export function documentId(value) {
  const raw = String(value ?? '');
  if (!/^[1-9]\d*$/.test(raw) || BigInt(raw) > 9223372036854775807n) {
    throw new Error('A valid saved record is required.');
  }
  return raw;
}

export function validateDocumentFile(file) {
  if (!file || typeof file.name !== 'string') throw new Error('Choose a file first.');
  const ext = file.name.split('.').pop().toLowerCase();
  const mime = FILE_MIME[ext];
  if (!mime) throw new Error('Use PDF, JPG, PNG, WebP, Word, Excel, TXT or CSV.');
  if (!Number.isFinite(file.size) || file.size <= 0) throw new Error('The file is empty.');
  if (file.size > MAX_DOCUMENT_BYTES) throw new Error('The maximum file size is 25 MB.');
  // The browser may leave MIME empty or use application/octet-stream.
  // This is only an input check, not a substitute for server content inspection.
  const actual = String(file.type || '').toLowerCase();
  const csvAlias = ext === 'csv' && ['text/plain', 'application/vnd.ms-excel'].includes(actual);
  if (actual && actual !== 'application/octet-stream' && actual !== mime && !csvAlias) {
    throw new Error('The file type does not match its extension. Choose the original file.');
  }
  return mime;
}

export function validDate(value) {
  if (!value) return true;
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return false;
  const time = new Date(`${value}T00:00:00Z`);
  return !Number.isNaN(time.getTime()) && time.toISOString().slice(0, 10) === value;
}

export function validateDocumentMetadata(values) {
  if (!DOCUMENT_TYPES.some(([key]) => key === values.document_type)) {
    throw new Error('Choose a document category.');
  }
  if (!String(values.title || '').trim()) throw new Error('Enter a document title.');
  if (String(values.title).trim().length > 200) throw new Error('Use a title of 200 characters or fewer.');
  if (String(values.description || '').length > 2000) throw new Error('Use notes of 2,000 characters or fewer.');
  if (String(values.version_label || '').length > 50) throw new Error('Use a version of 50 characters or fewer.');
  for (const key of ['effective_date', 'expiry_date', 'review_date']) {
    if (!validDate(values[key])) throw new Error('Enter valid calendar dates.');
  }
  if (values.effective_date && values.expiry_date && values.expiry_date < values.effective_date) {
    throw new Error('Expiry cannot be before the effective date.');
  }
}

export function documentTypeLabel(type) {
  return DOCUMENT_TYPES.find(([key]) => key === type)?.[1] || type || 'Supporting document';
}
export function fileSizeLabel(value) {
  const size = Number(value);
  if (!Number.isFinite(size) || size < 0) return 'Size unavailable';
  if (size < 1024) return `${size} B`;
  if (size < 1024 * 1024) return `${(size / 1024).toFixed(1)} KB`;
  return `${(size / (1024 * 1024)).toFixed(1)} MB`;
}
export function dateLabel(value) {
  if (!value) return 'Not set';
  const date = String(value).slice(0, 10);
  if (!validDate(date)) return 'Not set';
  const [year, month, day] = date.split('-');
  return `${day}/${month}/${year}`;
}
export function scheduleStatus(document, now = new Date()) {
  const today = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
  const days = (value) => {
    const text = String(value || '').slice(0, 10);
    return text && validDate(text) ? Math.round((Date.parse(`${text}T00:00:00Z`) - today) / 86400000) : null;
  };
  const expiry = days(document.expiry_date);
  const review = days(document.review_date);
  if (expiry !== null && expiry < 0) return { label: 'Expired', tone: 'danger' };
  if (expiry !== null && expiry <= 30) return { label: expiry === 0 ? 'Expires today' : `Expires in ${expiry} days`, tone: 'warning' };
  if (review !== null && review <= 0) return { label: 'Review due', tone: 'warning' };
  return { label: expiry === null ? 'No expiry set' : 'Expiry recorded', tone: 'neutral' };
}
export function safeDownloadName(value) {
  return String(value || 'document').replace(/[\x00-\x1f\x7f/\\:*?"<>|]/g, '_').slice(0, 180) || 'document';
}
export function vendorActive(value) {
  const raw = value?.active ?? value?.is_active;
  if (raw === true || raw === 'true') return true;
  if (raw === false || raw === 'false') return false;
  return null;
}
